import { PropsWithChildren, useEffect, useMemo } from "react";
import { RelayEnvironmentProvider } from "react-relay";
import { DataRouter, RouterProvider, RouterState } from "react-router";
import { Environment } from "relay-runtime";
import { AuthContext, AuthContextValue, useAuth } from "@/utils/auth";
import { LocationContext, LocationContextValue } from "@/utils/location";
import { AppHandle } from "./state";
import { Reactive, ReactiveMap, Sink, Subscription } from "@/utils/signal";
import { PostHog, PostHogProvider } from "@posthog/react";
import { eq, isEqual, pull } from "lodash";
import { useStatisticsConsent } from "../consent";
import { applyConsent } from "../posthog";

export function createProviders(
  app: AppHandle,
  posthog: PostHog,
  relay: Environment,
  router: DataRouter,
  redirect: (url: string, replace: boolean) => void,
  context: Window = window,
) {
  // Created once: the location value is compared with isEqual, which compares
  // functions by reference.
  const replace = (url: string) => redirect(url, true);
  const assign = (url: string) => redirect(url, false);

  const routerStateToLocation = (
    state: typeof router.state,
  ): LocationContextValue => {
    const url = new URL(
      state.location.pathname + state.location.search + state.location.hash,
      context.location.origin,
    );
    return {
      href: url.toString(),
      protocol: url.protocol,
      host: url.host,
      port: url.port,
      hostname: url.hostname,
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      origin: url.origin,
      replace,
      assign,
    };
  };

  return {
    AuthProvider({ children }: PropsWithChildren) {
      const auth = useMemo(
        (): AuthContextValue => ({
          userId: app.userId,
          isAuthenticated: app.isAuthenticated,
          getToken: () => app.token?.token,
          setTokens: ({ access, refresh }) =>
            app.setTokens(access ?? null, refresh ?? null),
          revokeToken: (type) => {
            switch (type) {
              case "access":
                return app.revokeAccess();
              case "refresh":
                return app.revokeAll();
            }
          },
          revokeTokens: () => app.revokeAll(),
        }),
        [],
      );
      return (
        <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
      );
    },

    RelayEnvironment({ children }: PropsWithChildren) {
      return (
        <RelayEnvironmentProvider environment={relay}>
          {children}
        </RelayEnvironmentProvider>
      );
    },

    LocationProvider({ children }: PropsWithChildren) {
      const signal = useMemo(() => new RouterSignal(router), []);
      useEffect(() => () => signal.flush(), [signal]);

      const location = useMemo(
        () => signal.map(routerStateToLocation, isEqual),
        [signal],
      );
      return (
        <LocationContext.Provider value={location}>
          {children}
        </LocationContext.Provider>
      );
    },

    PostHogProvider({ children }: PropsWithChildren) {
      const consent = useStatisticsConsent();
      const { userId: viewerId } = useAuth();

      useEffect(() => applyConsent(consent), [consent]);
      useEffect(() => {
        if (consent !== "granted" || !viewerId) return;
        posthog.identify(viewerId);
      }, [consent, viewerId]);

      return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
    },

    Router() {
      return <RouterProvider router={router} />;
    },
  };
}

class RouterSignal implements Reactive<RouterState> {
  private readonly sinks = new Array<Sink<RouterState>>();

  constructor(readonly router: DataRouter) {}

  flush() {
    for (const sink of this.sinks.splice(0, this.sinks.length)) {
      if (typeof sink === "function") continue;
      sink.complete?.();
    }
  }

  get value() {
    return this.router.state;
  }

  subscribe(sink: Sink<RouterState>): Subscription {
    this.sinks.push(sink);
    const unsub = this.router.subscribe((state) => {
      if (typeof sink === "function") {
        sink(state);
      } else {
        sink.next(state);
      }
    });
    return () => {
      try {
        try {
          if (typeof sink !== "function") {
            sink.complete?.();
          }
        } finally {
          unsub();
        }
      } finally {
        pull(this.sinks, sink);
      }
    };
  }

  map<T>(
    f: (state: RouterState) => T,
    shortcircuit: (lhs: T, rhs: T) => boolean = eq,
  ): Reactive<T> {
    return new ReactiveMap(this, f, shortcircuit);
  }
}
